import { useSelector, shallowEqual } from 'react-redux';
import moment from 'moment';

import { selectPsrPreferences } from 'state/psr/psr-slice';
import { NsDatepicker } from 'components/common/ns-datepicker/NsDatepicker';
import { DATE_FORMAT } from 'config/app-config';
import NsInputterEvent from 'components/common/ns-inputter/NsInputterEvent';
import NsContent from 'components/common/ns-content/NsContent';

import type { DateString } from 'components/common/ns-datepicker/NsDatepicker';
import type { PsrItem } from 'config/psr-config';

type Props = {
  psrItem: PsrItem;
  handleChange: (event: NsInputterEvent) => void;
};

export const PsrExpiryDate = ({ psrItem, handleChange }: Props) => {
  const today = moment();
  const minDate = today.format(DATE_FORMAT);
  const maxDate = today
    .clone()
    .add(6, 'years')
    .subtract(1, 'day')
    .format(DATE_FORMAT);

  const {
    displayName,
    code,
    subCode,
    subCodes,
    requiresExpiryDate,
    description,
    type
  } = psrItem;
  const psrPreferences = useSelector(selectPsrPreferences, shallowEqual);
  const hasSubCodes = Array.isArray(subCodes);
  const psrPref = psrPreferences[type]?.find((psrOption) => {
    if (hasSubCodes) {
      return psrOption.code === code;
    }
    return psrOption.code === code && psrOption.subCode === subCode;
  });
  const name = [type, code, subCode || psrPref?.subCode, 'expiry'].join('-');
  const value = (psrPref?.expiryDate as DateString) ?? ('' as DateString);
  const isChecked = !!psrPref;
  const shouldShow = requiresExpiryDate && isChecked;

  if (!shouldShow) return null;

  return (
    <NsContent>
      <NsDatepicker
        name={name}
        label={displayName}
        value={value}
        min={minDate as DateString}
        max={maxDate as DateString}
        onChange={handleChange}
        helper="Possible end date"
        tipDetails={
          <>
            <p>{description}</p>
            <p>
              Please put in a possible end date of when you think your situation
              might change
            </p>
          </>
        }
      />
    </NsContent>
  );
};
